import React from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

/**
 * Compact EN / FR pill toggle in the header. Also usable inline anywhere.
 * Persists selection under localStorage key `drk-lang` via i18next detector.
 */
export function LanguageSwitcher({ compact = false, className = "" } = {}) {
    const { i18n, t } = useTranslation();
    const current = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase().startsWith("fr")
        ? "fr"
        : "en";

    const set = (lng) => {
        if (lng === current) return;
        i18n.changeLanguage(lng);
    };

    return (
        <div
            className={`inline-flex items-center rounded-full border border-[color:var(--border-default)] bg-white ${
                compact ? "p-0.5 gap-0.5" : "p-1 gap-1"
            } ${className}`}
            role="group"
            aria-label={t("language.currentLanguageLabel", "Language")}
            data-testid="language-switcher"
        >
            <Globe className="ml-1.5 mr-0.5 h-3.5 w-3.5 text-[color:var(--text-soft)]" aria-hidden />
            {[
                { code: "en", label: "EN" },
                { code: "fr", label: "FR" },
            ].map(({ code, label }) => {
                const active = current === code;
                return (
                    <button
                        key={code}
                        type="button"
                        onClick={() => set(code)}
                        aria-pressed={active}
                        className={`rounded-full text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-teal-2)] ${
                            compact ? "px-2 py-0.5" : "px-2.5 py-1"
                        } ${
                            active
                                ? "bg-[color:var(--brand-navy)] text-white"
                                : "text-[color:var(--brand-navy)] hover:bg-[color:var(--bg-subtle)]"
                        }`}
                        data-testid={`lang-btn-${code}`}
                    >
                        {label}
                    </button>
                );
            })}
        </div>
    );
}
